"use client";

import { motion } from "motion/react";
import { Book } from "lucide-react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";

import { usePublicProfileStore } from "../store/usePublicProfileStore";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";

export function PublicSubjectsDisplay() {
  const { profileData } = usePublicProfileStore();
  const data = profileData?.subjects_section;
  const { t } = useTranslation(["profile"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  if (!data || data.length === 0) return null;

  return (
    <Card className="bg-side-accent/50 relative overflow-hidden rounded-2xl border-l-4 border-none border-l-orange-400/50 shadow-2xl backdrop-blur-xl">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br from-orange-400 to-amber-600" />
        <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500" />
      </div>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <motion.h2
          animate={{ y: 0, opacity: 1 }}
          className="text-accent-foreground mb-2 flex items-center gap-2 text-xl font-semibold"
          initial={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-center rounded-xl bg-orange-500/20 p-2">
            <Icon className="text-orange-400" height="1.5rem" icon="mdi:bookshelf" width="1.5rem" />
          </div>
          {t("profile:subjectsSection.title")}
        </motion.h2>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.map((subject, index) => (
          <motion.div
            key={subject.subject.id}
            animate={{ y: 0, opacity: 1 }}
            className="space-y-2 rounded-2xl border border-orange-300/20 bg-orange-500/10 p-4 backdrop-blur-sm transition-colors hover:bg-orange-500/15"
            initial={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className="flex items-start space-x-4">
              <div className="mt-1 flex items-center justify-center rounded-xl bg-orange-500/20 p-2">
                <Icon
                  className="text-orange-400"
                  height="1.5rem"
                  icon={subject.subject.icon}
                  width="1.5rem"
                />
              </div>
              <div className="space-y-1">
                <h3 className="text-accent-foreground text-lg font-semibold">
                  {subject.subject.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {subject.curriculumLevels.map((level) => (
                    <Badge
                      key={level.id}
                      className="flex items-center gap-2 rounded-xl border border-orange-400/30 bg-orange-500/20 px-3 py-1 font-medium text-orange-300 backdrop-blur-sm"
                      variant="outline"
                    >
                      <Book className="h-3 w-3" />
                      {level.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </CardContent>
    </Card>
  );
}
